/**
 * API Client
 * Centralized axios instance with interceptors for authentication and error handling
 */

import axios from 'axios';
import Swal from 'sweetalert2';
import { floatingNotice } from '@/helpers/notify';
import { tokenManager } from '@/utils/token-manager';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Request interceptor
 * Adds Authorization header with access token
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * Handles token refresh on 401 errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip token refresh only for endpoints where 401 means bad credentials, not an
    // expired session. /auth/me must still refresh: it is the first call after F5.
    if (/\/auth\/(login|2fa\/|refresh)/.test(originalRequest.url || '')) {
      return Promise.reject(error);
    }

    // Handle token refresh
    if (isRefreshing) {
      // Add to queue if already refreshing
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Refresh the token
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL || '/api/v1'}/auth/refresh`,
        { refresh_token: refreshToken }
      );

      const { access_token, refresh_token: newRefreshToken } = response.data;
      tokenManager.setTokens(access_token, newRefreshToken);

      // Process queued requests
      processQueue(null, access_token);

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh failed - clear tokens and redirect to login
      processQueue(refreshError, null);
      tokenManager.clearTokens();

      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ---------------------------------------------------------------------------
// HRM v3 — sửa đơn đã chuyển xưởng (backend services/hrm/edit_gate.py).
// 409 HRM_EDIT_CONFIRM ⇒ hỏi xác nhận rồi gửi lại ĐÚNG request kèm header
// X-Hrm-Edit-Confirmed. Mọi popup đi qua MỘT hàng đợi: đổi trạng thái hàng loạt
// (Promise.all) nhận nhiều 409 cùng lúc, mà Swal.fire mới huỷ popup đang mở và
// promise của popup bị huỷ không bao giờ xong ⇒ spinner treo.
// ---------------------------------------------------------------------------
const HRM_GROUP_LABELS = {
  items: "sản phẩm · số lượng · size · tuỳ chọn",
  printList: "danh sách in",
  deliveryDate: "ngày giao",
  source: "nguồn đơn",
  customer: "khách hàng",
  recipient: "người nhận · địa chỉ giao",
  staff: "người chốt đơn · nhân viên phụ trách",
};

const escapeHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
));

let hrmPopupQueue = Promise.resolve();

const askHrmConfirm = (data) => {
  const run = () => {
    const groups = (data.groups || []).map((g) => HRM_GROUP_LABELS[g] || g).join(", ");
    const hrmMsg = data.hrm?.message;
    const html = [
      groups ? `<div>Bản sửa này đổi: <strong>${escapeHtml(groups)}</strong>.</div>` : "",
      hrmMsg ? `<div class="mt-2 small text-muted">Xưởng: ${escapeHtml(hrmMsg)}</div>` : "",
      data.hrm ? "" : '<div class="mt-2 small text-warning">Chưa hỏi được xưởng — lưu xong xưởng sẽ báo lại phần nào không nhận trên đơn.</div>',
    ].join("");
    return Swal.fire({
      title: data.detail || "Đơn đã chuyển xưởng HRM. Xác nhận sửa?",
      html, icon: "warning", showCancelButton: true,
      confirmButtonText: "Xác nhận sửa", cancelButtonText: "Huỷ",
    }).then((r) => r.isConfirmed);
  };
  const turn = hrmPopupQueue.then(run);
  hrmPopupQueue = turn.catch(() => {});
  return turn;
};

apiClient.interceptors.response.use(
  (response) => {
    if (response.headers?.["x-hrm-warning"] === "hrm_unreachable") {
      floatingNotice("Không hỏi được xưởng HRM. Đã lưu — xưởng sẽ báo lại trên đơn phần nào không nhận.");
    }
    return response;
  },
  async (error) => {
    const data = error.response?.data;
    const config = error.config;
    if (error.response?.status !== 409 || data?.code !== "HRM_EDIT_CONFIRM" || !config || config._hrmConfirmed) {
      return Promise.reject(error);
    }
    if (!(await askHrmConfirm(data))) {
      // notifyError bỏ qua lỗi này; chỗ nào đọc thẳng `detail` vẫn ra câu đúng nghĩa.
      error.hrmCancelled = true;
      data.detail = "Đã huỷ — chưa lưu thay đổi.";
      return Promise.reject(error);
    }
    config._hrmConfirmed = true;
    config.headers = { ...(config.headers || {}), "X-Hrm-Edit-Confirmed": "1" };
    return apiClient(config);
  }
);

/**
 * Utility function to extract data from axios response
 * @param {Object} response - Axios response object
 * @returns {Object} Response data
 */
export const extractData = (response) => response.data;

/**
 * Utility function to handle API errors
 * @param {Object} error - Error object
 * @returns {Object} Formatted error object
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    return {
      status,
      message: data?.message || data?.detail || 'An error occurred',
      errors: data?.detail || null
    };
  } else if (error.request) {
    // Request made but no response
    return {
      status: 0,
      message: 'No response from server. Please check your connection.',
      errors: null
    };
  } else {
    // Error setting up request
    return {
      status: -1,
      message: error.message || 'An unexpected error occurred',
      errors: null
    };
  }
};

export default apiClient;
